import { useEffect, useState } from 'react'
import { Check, Loader2, Plus, Trash2, X } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../lib/shadcn/dialog'
import { Button } from '../../lib/shadcn/button'
import { Input } from '../../lib/shadcn/input'
import { toast } from '../../lib/shadcn/sonner'
import {
  useUpsertIngredientCategory,
  useDeleteIngredientCategory,
} from '../../hooks/backend/recipes'
import { HelpText } from '../../components/HelpText'

export interface IngredientCategory {
  id: string
  name: string
  sort_order: number
  ingredient_count: number
}

interface IngredientCategoriesDialogProps {
  categories: IngredientCategory[]
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Called after any add, rename or delete so the panel can reload. */
  onChanged: () => Promise<unknown> | void
}

function errorText(error: unknown) {
  return error instanceof Error ? error.message : 'Unknown error'
}

export default function IngredientCategoriesDialog({
  categories,
  open,
  onOpenChange,
  onChanged,
}: IngredientCategoriesDialogProps) {
  const upsertCategory = useUpsertIngredientCategory()
  const deleteCategory = useDeleteIngredientCategory()

  const [newName, setNewName] = useState('')
  const [names, setNames] = useState<Record<string, string>>({})
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)

  useEffect(() => {
    if (open) {
      setNewName('')
      setConfirmDelete(null)
    }
  }, [open])

  useEffect(() => {
    setNames(Object.fromEntries(categories.map(c => [c.id, c.name])))
  }, [categories])

  const nameTaken = (name: string, exceptId?: string) =>
    categories.some(
      c => c.id !== exceptId && c.name.trim().toLowerCase() === name.trim().toLowerCase(),
    )

  const add = async () => {
    const name = newName.trim()
    if (!name) return
    if (nameTaken(name)) {
      toast.error(`A category named “${name}” already exists`)
      return
    }
    try {
      setBusy('add')
      await upsertCategory.trigger({ name })
      setNewName('')
      await onChanged()
    } catch (error) {
      toast.error(`Failed to add category: ${errorText(error)}`)
    } finally {
      setBusy(null)
    }
  }

  const rename = async (cat: IngredientCategory) => {
    const name = (names[cat.id] ?? '').trim()
    if (!name || name === cat.name) {
      setNames(n => ({ ...n, [cat.id]: cat.name }))
      return
    }
    if (nameTaken(name, cat.id)) {
      toast.error(`A category named “${name}” already exists`)
      return
    }
    try {
      setBusy(cat.id)
      await upsertCategory.trigger({ id: cat.id, name })
      toast.success(`Renamed to ${name}`)
      await onChanged()
    } catch (error) {
      toast.error(`Failed to rename: ${errorText(error)}`)
    } finally {
      setBusy(null)
    }
  }

  const remove = async (cat: IngredientCategory) => {
    try {
      setBusy(cat.id)
      await deleteCategory.trigger({ id: cat.id })
      toast.success(`Deleted ${cat.name}`)
      setConfirmDelete(null)
      await onChanged()
    } catch (error) {
      toast.error(`Failed to delete: ${errorText(error)}`)
    } finally {
      setBusy(null)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Ingredient Categories</DialogTitle>
          <DialogDescription><HelpText>
            Add or rename categories here, then pick one for each ingredient in its
            Edit dialog. Deleting a category moves its ingredients to Uncategorized.
          </HelpText></DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto min-h-0 space-y-4 py-2">
          <form
            className="flex gap-2"
            onSubmit={e => {
              e.preventDefault()
              void add()
            }}
          >
            <Input
              value={newName}
              onChange={e => setNewName(e.target.value)}
              placeholder="New category, e.g. Chocolate"
              aria-label="New category name"
            />
            <Button type="submit" disabled={!newName.trim() || busy === 'add'} className="gap-1.5 shrink-0">
              {busy === 'add' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              Add
            </Button>
          </form>

          {categories.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">
              No categories yet.
            </p>
          ) : (
            <ul className="divide-y rounded-md border">
              {categories.map(cat => {
                const value = names[cat.id] ?? cat.name
                const changed = value.trim() !== cat.name
                return (
                  <li key={cat.id} className="flex items-center gap-2 px-2 py-1.5">
                    <Input
                      value={value}
                      onChange={e => setNames(n => ({ ...n, [cat.id]: e.target.value }))}
                      onKeyDown={e => {
                        if (e.key === 'Enter') {
                          e.preventDefault()
                          void rename(cat)
                        } else if (e.key === 'Escape' && changed) {
                          e.preventDefault()
                          e.stopPropagation()
                          setNames(n => ({ ...n, [cat.id]: cat.name }))
                        }
                      }}
                      className="h-8 text-sm"
                      aria-label={`Rename ${cat.name}`}
                    />
                    <span className="shrink-0 w-16 text-right text-xs text-muted-foreground tabular-nums">
                      {cat.ingredient_count} item{cat.ingredient_count === 1 ? '' : 's'}
                    </span>
                    {busy === cat.id ? (
                      <Loader2 className="w-4 h-4 mx-2 animate-spin text-muted-foreground" />
                    ) : changed ? (
                      <>
                        <Button size="icon" className="h-8 w-8 shrink-0" onClick={() => void rename(cat)} aria-label="Save name" title="Save name">
                          <Check className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 shrink-0"
                          onClick={() => setNames(n => ({ ...n, [cat.id]: cat.name }))}
                          aria-label="Cancel rename"
                          title="Cancel"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </>
                    ) : confirmDelete === cat.id ? (
                      <>
                        <Button
                          variant="destructive"
                          size="sm"
                          className="h-8 shrink-0"
                          onClick={() => void remove(cat)}
                        >
                          Delete
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 shrink-0"
                          onClick={() => setConfirmDelete(null)}
                          aria-label="Keep category"
                          title="Keep"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </>
                    ) : (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive"
                        onClick={() => setConfirmDelete(cat.id)}
                        aria-label={`Delete ${cat.name}`}
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </li>
                )
              })}
            </ul>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
